import React, { useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CartContext } from './CartContext';
import { products } from '../products';
import '../css/ProductPage.css';

const ProductPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const product = products.find(p => p.id === id);
    const [selectedWeight, setSelectedWeight] = useState('ounce');
    const [quantity, setQuantity] = useState(1);
    const [cartItems, setCartItems] = useState([]); // Track multiple selections
    const { addToCart } = useContext(CartContext);

    if (!product) {
        return (
            <div className="error-container">
                <div className="error-content">
                    <h1>Product Not Found</h1>
                    <p>The product you're looking for doesn't exist.</p>
                    <button onClick={() => navigate('/')} className="back-home-btn">
                        Back to Home
                    </button>
                </div>
            </div>
        );
    }

    const isFlower = product.category === 'flower';
    const isTopShelf = product.category === 'TopShelf flower';
    const isExotic = product.category === 'exotic';

    // FIXED: Calculate price options with clear logic
    const getPriceOptions = () => {
        if (isTopShelf) {
            return {
                ounce: product.price,
                half: 70,
                quarter: 50,
                eighth: 40
            };
        } else if (isExotic) {
            return {
                ounce: product.price,
                half: 100,
                quarter: 70,
                eighth: 50
            };
        } else if (isFlower) {
            return {
                ounce: product.price,
                half: product.price * 0.6,
                quarter: product.price * 0.4,
                eighth: product.price * 0.3
            };
        } else {
            return { default: product.price };
        }
    };

    const priceOptions = getPriceOptions();
    const currentPrice = ((isFlower || isTopShelf || isExotic) ? priceOptions[selectedWeight] : product.price) * quantity;

    const handleAddToCart = () => {
        const cartItem = {
            id: `${product.id}-${selectedWeight}`, // Unique ID with weight
            productId: product.id, // Original product ID for reference
            name: product.name,
            image: product.image,
            price: (isFlower || isTopShelf || isExotic) ? priceOptions[selectedWeight] : product.price,
            ...((isFlower || isTopShelf || isExotic) && { weight: selectedWeight }),
            quantity: quantity,
            countInStock: product.countInStock,
            totalPrice: currentPrice
        };

        addToCart(cartItem);

        // Add to local state for display
        setCartItems(prev => [...prev, {
            ...cartItem,
            timestamp: Date.now() // For unique key
        }]);

        // Reset quantity after adding
        setQuantity(1);
    };

    const handleAddAnother = () => {
        // Just reset quantity for adding another item
        setQuantity(1);
    };

    const removeCartItem = (timestamp) => {
        setCartItems(prev => prev.filter(item => item.timestamp !== timestamp));
    };

    const getTotalCartPrice = () => {
        return cartItems.reduce((total, item) => total + item.totalPrice, 0);
    };

    // Debug: Check if exotic products are getting weight options
    console.log('Product Details:', {
        name: product.name,
        category: product.category,
        isExotic: isExotic,
        hasWeightOptions: (isFlower || isTopShelf || isExotic),
        priceOptions: priceOptions,
        selectedWeight: selectedWeight
    });

    return (
        <div className="product-detail-page">
            <div className="container">
                <button
                    onClick={() => navigate(-1)}
                    className="back-button"
                >
                    <span className="back-arrow">←</span>
                    Back to Products
                </button>

                <div className="product-layout">
                    <div className="product-gallery">
                        <div className="main-image">
                            <img
                                src={product.image}
                                alt={product.name}
                                className="gallery-image"
                            />
                        </div>
                    </div>

                    <div className="product-info">
                        <div className="product-header">
                            <div className="title-section">
                                <h1 className="product-title">{product.name}</h1>
                                {isExotic && (
                                    <div className="exotic-badge">🔥 Exotic</div>
                                )}
                            </div>

                            <div className="meta-tags">
                                <span className="strain-tag">{product.brand}</span>
                                <span className="category-tag">{product.category}</span>
                                <div className="rating">
                                    {'★'.repeat(product.rating)}{'☆'.repeat(5 - product.rating)}
                                    <span className="review-count">({product.numReviews})</span>
                                </div>
                            </div>
                        </div>

                        <div className="product-description">
                            <p>{product.description}</p>
                        </div>

                        <div className="purchase-options">
                            {(isFlower || isTopShelf || isExotic) && (
                                <div className="option-section">
                                    <h3 className="section-title">Select Weight</h3>
                                    <div className="weight-options">
                                        {[
                                            { value: 'ounce', label: 'Ounce', grams: '28g', price: priceOptions.ounce },
                                            { value: 'half', label: 'Half', grams: '14g', price: priceOptions.half },
                                            { value: 'quarter', label: 'Quarter', grams: '7g', price: priceOptions.quarter },
                                            { value: 'eighth', label: 'Eighth', grams: '3.5g', price: priceOptions.eighth }
                                        ].map(option => (
                                            <div
                                                key={option.value}
                                                className={`weight-option ${selectedWeight === option.value ? 'selected' : ''}`}
                                                onClick={() => setSelectedWeight(option.value)}
                                            >
                                                <div className="option-content">
                                                    <div className="weight-label">{option.label}</div>
                                                    <div className="weight-grams">{option.grams}</div>
                                                    <div className="weight-price">${option.price.toFixed(2)}</div>
                                                </div>
                                                <div className="selection-indicator"></div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="option-section">
                                <h3 className="section-title">Quantity</h3>
                                <div className="quantity-selector">
                                    <button
                                        className="quantity-btn"
                                        onClick={() => setQuantity(q => Math.max(1, q - 1))}
                                        disabled={quantity <= 1}
                                    >
                                        −
                                    </button>
                                    <span className="quantity-display">{quantity}</span>
                                    <button
                                        className="quantity-btn"
                                        onClick={() => setQuantity(q => Math.min(5, q + 1))}
                                        disabled={quantity >= 5}
                                    >
                                        +
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="purchase-section">
                            <div className="price-display">
                                <div className="total-price">${currentPrice.toFixed(2)}</div>
                                <div className="price-breakdown">
                                    {quantity} × ${((isFlower || isTopShelf || isExotic) ? priceOptions[selectedWeight] : product.price).toFixed(2)} {(isFlower || isTopShelf || isExotic) && `/ ${selectedWeight}`}
                                </div>
                            </div>

                            <div className="stock-status">
                                <div className={`status-indicator ${product.countInStock > 0 ? 'in-stock' : 'out-of-stock'}`}>
                                    {product.countInStock > 0 ?
                                        `✅ In Stock (${product.countInStock} available)` :
                                        '❌ Out of Stock'
                                    }
                                </div>
                            </div>

                            <div className="action-buttons">
                                <button
                                    className={`purchase-btn ${product.countInStock <= 0 ? 'disabled' : ''} ${isExotic ? 'exotic-btn' : ''}`}
                                    disabled={product.countInStock <= 0}
                                    onClick={handleAddToCart}
                                >
                                    {product.countInStock > 0 ? (
                                        <>
                                            <span className="btn-main-text">Add to Cart</span>
                                            <span className="btn-price-text">${currentPrice.toFixed(2)}</span>
                                        </>
                                    ) : (
                                        <span className="disabled-text">Currently Unavailable</span>
                                    )}
                                </button>

                                {cartItems.length > 0 && (
                                    <button
                                        className="add-another-btn"
                                        onClick={handleAddAnother}
                                    >
                                        Add Another Item
                                    </button>
                                )}
                            </div>

                            {/* Display added items */}
                            {cartItems.length > 0 && (
                                <div className="added-items-section">
                                    <h3 className="section-title">Items Added to Cart</h3>
                                    <div className="added-items-list">
                                        {cartItems.map((item) => (
                                            <div key={item.timestamp} className="added-item">
                                                <div className="item-details">
                                                    <span className="item-name">{item.name}</span>
                                                    {item.weight && (
                                                        <span className="item-weight"> - {item.weight} ({item.quantity}x)</span>
                                                    )}
                                                    <span className="item-price">${item.totalPrice.toFixed(2)}</span>
                                                </div>
                                                <button
                                                    className="remove-item-btn"
                                                    onClick={() => removeCartItem(item.timestamp)}
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="cart-total">
                                        <strong>Subtotal: ${getTotalCartPrice().toFixed(2)}</strong>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductPage;